import { z } from 'zod';

import { buildQueryString } from '../../utils/querystring';
import { makeEndpoint, paginateQuerySchema, paginateResponseSchema } from '../common';

// ---------------------------------------------------------------------------
// Shared enums & schemas
// ---------------------------------------------------------------------------

export const ListingMapClassifierEnum = z.enum(['cbsa', 'eurostat', 'oecd']);
export type ListingMapClassifier = z.infer<typeof ListingMapClassifierEnum>;

// ---------------------------------------------------------------------------
// GET /v2/listing-map/points
// ---------------------------------------------------------------------------

export const ListingMapPointsQuerySchema = z
  .object({
    brandId: z.string().uuid().optional(),
    /** Full compound values stored in listing_classification.value, e.g. ["cbsa:35620", "cbsa:31080"] */
    marketCodes: z.array(z.string()).optional(),
    /** Full country name as stored in location.country, e.g. "Germany" */
    country: z.string().optional(),
    /** When true, only return listings with status = 'published' */
    onlyPublished: z
      .enum(['true', 'false'])
      .transform((v) => v === 'true')
      .optional(),
    /** UUIDs of ListingClassificationLabels; when supplied, resolves to values server-side */
    labelIds: z.array(z.string().uuid()).optional(),
    /** Direct classification filter as compound 'domain:value' strings; AND across domains, OR within */
    classificationValues: z.array(z.string()).optional(),
  })
  .strict();

export const ListingMapPointSchema = z.object({
  /** listingId */
  i: z.string(),
  /** listing title */
  t: z.string().nullable(),
  /** longitude */
  x: z.number(),
  /** latitude */
  y: z.number(),
  /** marketCode (value from listing_classification), nullable when listing has no market classification */
  m: z.string().nullable(),
  /** classifier (e.g. "cbsa"), nullable when listing has no market classification */
  c: z.string().nullable(),
});

export type ListingMapPoint = z.infer<typeof ListingMapPointSchema>;

export const ListingMapPointsResponseSchema = z.object({
  points: z.array(ListingMapPointSchema),
});

export const GetListingMapPointsEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ query }: { query: z.input<typeof ListingMapPointsQuerySchema> }) =>
    `/v2/listing-map/points?${buildQueryString(query)}`,
  requestSchema: z.void(),
  responseSchema: ListingMapPointsResponseSchema,
  querySchema: ListingMapPointsQuerySchema,
});

// ---------------------------------------------------------------------------
// GET /v2/listing-map/listings/:listingId  (detail on click)
// ---------------------------------------------------------------------------

export const ListingMapDetailResponseSchema = z.object({
  id: z.string(),
  title: z.string().nullable(),
  lng: z.number(),
  lat: z.number(),
  location: z.object({
    name: z.string(),
    city: z.string().nullable(),
    country: z.string().nullable(),
  }),
  brand: z.object({
    id: z.string(),
    name: z.string(),
  }),
  classifications: z.array(
    z.object({
      domain: z.string(),
      classifier: z.string(),
      value: z.string(),
      displayName: z.string().nullable(),
    }),
  ),
});

export const GetListingMapDetailEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ listingId }: { listingId: string }) => `/v2/listing-map/listings/${listingId}`,
  requestSchema: z.void(),
  responseSchema: ListingMapDetailResponseSchema,
});

// ---------------------------------------------------------------------------
// GET /v2/listing-map/filters  (populates sidebar dropdowns)
// ---------------------------------------------------------------------------

export const ListingMapFiltersResponseSchema = z.object({
  brands: z.array(z.object({ id: z.string(), name: z.string() })),
  countries: z.array(z.string()),
  markets: z.array(
    z.object({
      classifier: z.string(),
      code: z.string(),
      name: z.string().nullable(),
    }),
  ),
});

export const GetListingMapFiltersEndpoint = makeEndpoint({
  method: 'GET',
  url: () => `/v2/listing-map/filters`,
  requestSchema: z.void(),
  responseSchema: ListingMapFiltersResponseSchema,
});

// ---------------------------------------------------------------------------
// GET /v2/listing-map/list  (paginated list view)
// ---------------------------------------------------------------------------

export const ListingMapListItemSchema = z.object({
  id: z.string(),
  title: z.string().nullable(),
  brandId: z.string(),
  brandName: z.string(),
  locationName: z.string().nullable(),
});

export type ListingMapListItem = z.infer<typeof ListingMapListItemSchema>;

export const ListingMapListQuerySchema = paginateQuerySchema({}).extend({
  brandId: z.string().uuid().optional(),
  marketCodes: z.array(z.string()).optional(),
  country: z.string().optional(),
  onlyPublished: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional(),
  labelIds: z.array(z.string().uuid()).optional(),
  classificationValues: z.array(z.string()).optional(),
  sortBy: z.enum(['title', 'brandName']).optional(),
});

export const ListingMapListResponseSchema = paginateResponseSchema(ListingMapListItemSchema);

export const GetListingMapListEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ query }: { query: z.input<typeof ListingMapListQuerySchema> }) =>
    `/v2/listing-map/list?${buildQueryString(query)}`,
  requestSchema: z.void(),
  responseSchema: ListingMapListResponseSchema,
  querySchema: ListingMapListQuerySchema,
});
